import { type ChangeEventHandler, useCallback } from 'react';
import {
  TextField,
  Tooltip,
  Typography,
} from '@forethought-technologies/forethought-elements';
import { IconX } from '@tabler/icons-react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  changeAdditionalFeedback,
  selectQuickFeedbackConfig,
  selectQuickFeedbackSteps,
  selectWorkflowsConfig,
} from 'reducers/workflows';
import { icons, useStyles } from './icons';
import { PopupModal } from 'components/PopupModal';
import { OrgThemedButton } from 'components/OrgThemedButton';

export default function AdditionalFeedbackModal({
  send,
  stepIndex,
}: {
  send: (isUseful?: boolean) => void;
  stepIndex: number;
}): JSX.Element {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const quickFeedbackConfig = useAppSelector(selectQuickFeedbackConfig);
  const quickFeedbackSteps = useAppSelector(selectQuickFeedbackSteps);
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);

  const { badStyles, goodStyles } = useStyles();

  const quickFeedbackStep = quickFeedbackSteps[stepIndex];

  const handleAdditionalFeedbackChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >(
    event => {
      dispatch(
        changeAdditionalFeedback({
          additionalFeedback: event.target.value,
          stepIndex,
        }),
      );
    },
    [dispatch, stepIndex],
  );

  const handleAdditionalFeedbackSubmit = useCallback(() => {
    send();
  }, [send]);

  if (!quickFeedbackConfig || !workflowsConfig) {
    return <></>;
  }

  const shouldRequestAdditionalFeedback = quickFeedbackStep?.isUseful
    ? quickFeedbackConfig.should_request_feedback_for_high_ratings
    : quickFeedbackConfig.should_request_feedback_for_low_ratings;

  const isAdditionalFeedbackModalVisible =
    quickFeedbackStep &&
    !quickFeedbackStep.isSent &&
    shouldRequestAdditionalFeedback;

  if (!isAdditionalFeedbackModalVisible) {
    return <></>;
  }

  const { BadIcon, GoodIcon } = icons[quickFeedbackConfig.style];

  const AdditionalFeedbackIcon = quickFeedbackStep?.isUseful
    ? GoodIcon
    : BadIcon;

  const additionalFeedbackQuestion = quickFeedbackStep?.isUseful
    ? quickFeedbackConfig.question_for_high_ratings
    : quickFeedbackConfig.question_for_low_ratings;

  return (
    <PopupModal
      onClickAway={handleAdditionalFeedbackSubmit}
      open={isAdditionalFeedbackModalVisible}
      position='bottom'
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box alignItems='center' display='flex' gap='8px' width='100%'>
          <AdditionalFeedbackIcon
            {...(quickFeedbackStep.isUseful ? goodStyles : badStyles)}
            strokeWidth='1.5px'
          />
          <Box flexGrow={1}>
            <Typography variant='font16Bold'>
              {
                workflowsConfig.forethought_text
                  ?.additional_feedback_dialog_title
              }
            </Typography>
          </Box>
          <Tooltip
            tooltipContent={
              workflowsConfig.forethought_text?.close_button_title || ''
            }
          >
            <Box
              aria-label={workflowsConfig.forethought_text?.close_button_title}
              component='button'
              // Submit on close intentionally:
              onClick={handleAdditionalFeedbackSubmit}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                color: theme.palette.colors.grey[800],
                cursor: 'pointer',
                height: '20px',
                padding: 0,
              }}
              type='button'
            >
              <IconX size={20} />
            </Box>
          </Tooltip>
        </Box>
        <TextField
          label={additionalFeedbackQuestion}
          maxLength={512}
          multiline
          onChange={handleAdditionalFeedbackChange}
          rows={4}
          value={quickFeedbackStep.additionalFeedback}
        />
        <OrgThemedButton
          fullWidth
          onClick={handleAdditionalFeedbackSubmit}
          variant='contained'
        >
          {workflowsConfig.forethought_text?.submit_button_title || ''}
        </OrgThemedButton>
      </Box>
    </PopupModal>
  );
}
